import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ServiceModal from './ServiceModal';

const ServicesSection: React.FC = () => {
  const [selectedService, setSelectedService] = useState<any>(null);
  const [showServiceModal, setShowServiceModal] = useState(false);

  const services = [
    {
      title: 'Custom Software Development',
      subtitle: 'Tailored Solutions',
      description: 'Bespoke software solutions designed to meet your specific business needs and requirements. From web applications to complex enterprise systems, we deliver scalable and maintainable solutions.',
      features: [
        'Web application development',
        'Mobile app development',
        'API integration',
        'Database design & optimization'
      ],
      benefits: [
        'Perfect fit for your business',
        'Scalable architecture',
        'Ongoing technical support',
        'Future-ready technology',
        'Competitive advantage'
      ],
      timeline: '4-12 weeks depending on complexity',
      pricing: '₱25,000 - ₱500,000+ based on requirements',
      icon: '⚙️',
      image: '/assets/IbayTech-Logo.png'
    },
    {
      title: 'Ibay App',
      subtitle: 'Mobile Marketplace',
      description: 'Get your Favorite Stuff from your Favorite Stores. The first online marketplace in Iba, Zambales since 2022, connecting local businesses with customers through advanced mobile technology.',
      features: [
        'User-friendly mobile interface',
        'Merchant store integration',
        'Real-time order tracking'
      ],
      benefits: [
        'Boost local business visibility',
        'Increase sales revenue',
        'Build customer loyalty',
        'Streamline operations',
        'Expand market reach'
      ],
      icon: '📱',
      image: '/assets/iBayApp.png'
    },
    {
      title: 'LGU Services Portal',
      subtitle: 'Government Digital Services',
      description: 'Ease of Doing Business a step further. Streamlining government services through digital automation, making bureaucratic processes faster and more transparent for citizens.',
      features: [
        'Online Business Permit Applications',
        'Online Community Tax',
        'Online Barangay Clearance',
        'Online Occupational Permit',
        'Online Building Permit'
      ],
      benefits: [
        'Reduce processing time by 80%',
        'Eliminate paper-based workflows',
        'Improve transparency',
        'Enhance citizen satisfaction',
        'Ensure compliance tracking'
      ],
      icon: '🏛️',
      image: '/assets/LGU-Automation.png'
    }
  ];

  const openServiceModal = (service: any) => {
    setSelectedService(service);
    setShowServiceModal(true);
  };

  return (
    <>
      <section className="section-padding bg-gray-900">
        <div className="container-custom">
          {/* Section Header */}
        <motion.div
          className="text-center mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-white mb-4 lg:mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Our{' '}
            <span className="bg-gradient-to-r from-blue-400 via-red-400 to-white bg-clip-text text-transparent">
              Services
            </span>
          </motion.h2>
          
          <motion.p
            className="text-base md:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            Innovative digital solutions that empower communities and transform the way people 
            interact with technology and government services.
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <div className="space-y-8 lg:space-y-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              {/* Content */}
              <div className={`space-y-4 lg:space-y-6 order-2 lg:order-none ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="flex items-start space-x-3">
                  <div className="text-2xl lg:text-3xl xl:text-4xl flex-shrink-0">{service.icon}</div>
                  <div className="min-w-0">
                    <h3 className="text-2xl lg:text-3xl xl:text-4xl font-display font-bold text-white leading-tight">
                      {service.title}
                    </h3>
                    <p className="text-sm lg:text-base xl:text-lg text-gray-400">{service.subtitle}</p>
                  </div>
                </div>

                <p className="text-sm lg:text-base xl:text-lg text-gray-300 leading-relaxed">
                  {service.description}
                </p>

                <div className="space-y-2 lg:space-y-3">
                  <h4 className="text-base lg:text-lg xl:text-xl font-semibold text-white">Key Features:</h4>
                  <ul className="space-y-1 lg:space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <motion.li
                        key={featureIndex}
                        className="flex items-center space-x-2 lg:space-x-3 text-gray-300 text-sm lg:text-base"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.6 + featureIndex * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <svg className="w-4 h-4 lg:w-5 lg:h-5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="leading-tight">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <motion.div
                  className="pt-3 lg:pt-6"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <button 
                    onClick={() => openServiceModal(service)}
                    className="inline-flex items-center justify-center px-4 lg:px-6 py-2 lg:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition-all duration-300 ease-in-out shadow-lg shadow-blue-500/25 text-sm lg:text-base"
                  >
                    Learn More
                    <svg className="ml-1 lg:ml-2 w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </motion.div>
              </div>

              {/* Image */}
              <motion.div
                className={`relative order-1 lg:order-none ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="relative">
                  <div className={`w-full h-48 sm:h-64 lg:h-80 xl:h-96 rounded-2xl overflow-hidden glass-card ${
                    service.title === 'Ibay App' 
                      ? 'bg-white' 
                      : 'bg-gradient-to-br from-gray-800 to-black'
                  }`}>
                    <div className="w-full h-full flex items-center justify-center p-2 lg:p-4">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className={`w-full h-full rounded-lg ${
                          service.title === 'LGU Services Portal' 
                            ? 'object-cover' 
                            : service.title === 'Ibay App'
                            ? 'object-contain'
                            : 'object-contain'
                        }`}
                        style={service.title === 'Ibay App' ? {
                          filter: 'none',
                          mixBlendMode: 'normal',
                          background: 'transparent'
                        } : {}}
                      />
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <motion.div
                    className="absolute -top-2 -right-2 lg:-top-4 lg:-right-4 w-6 h-6 lg:w-8 lg:h-8 bg-red-400 rounded-full opacity-30"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <motion.div
                    className="absolute -bottom-2 -left-2 lg:-bottom-4 lg:-left-4 w-4 h-4 lg:w-6 lg:h-6 border-2 border-blue-400 rounded-full opacity-40"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  />
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          className="text-center pt-8 lg:pt-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-4 lg:mb-6 px-4">
            Ready to Transform Your Business?
          </h3>
          <p className="text-sm lg:text-base xl:text-lg text-gray-300 mb-6 lg:mb-8 max-w-2xl mx-auto px-4 leading-relaxed">
            Let's discuss how our innovative solutions can help you achieve your goals and 
            drive your business forward.
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-200 transition-all duration-300 ease-in-out text-sm lg:text-base"
            >
              Get Started Today
              <svg className="ml-1 lg:ml-2 w-4 h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </motion.div>
      </div>
      </section>

      <ServiceModal
        isOpen={selectedService !== null}
        onClose={() => setSelectedService(null)}
        service={selectedService}
      />
    </>
  );
};

export default ServicesSection;
